import React, { useState, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Activity,
  RefreshCw,
  Sparkles,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { useSignalIntelligence, type SignalEntity } from '@/hooks/useSignalIntelligence';
import { cn } from '@/lib/utils';
import { SignalTabContent } from '@/components/signal/SignalTabContent';

const getEntityEmoji = (type: SignalEntity['type']) => {
  switch (type) {
    case 'self': return '🏦';
    case 'talent': return '🏃';
    case 'brand': return '🏢';
    case 'venture': return '💼';
    case 'agent': return '🤝';
    default: return '📊';
  }
};

const computeSentiment = (results: any[]) => {
  const positive = results.filter(r => r.sentiment === 'positive').length;
  const negative = results.filter(r => r.sentiment === 'negative').length;
  const neutral = results.length - positive - negative;
  const overallScore = results.length > 0 ? (positive / results.length) * 100 : 0;
  const overallSentiment = positive >= negative && positive >= neutral ? 'positive' : negative > positive ? 'negative' : 'neutral';
  return { positive, neutral, negative, overallScore, overallSentiment: overallSentiment as 'positive' | 'neutral' | 'negative' };
};

export const VentureSignalSection = () => {
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('7d');
  const { data, isLoading, refresh } = useSignalIntelligence(dateRange);
  const [activeTab, setActiveTab] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all-partners');

  const entities = data?.entities || [];
  const allResults = data?.combinedResults || [];
  const allAlerts = data?.alerts || [];

  const selfEntity = useMemo(() => entities.find(e => e.type === 'self'), [entities]);
  const partnerEntities = useMemo(() => entities.filter(e => e.type !== 'self'), [entities]);

  const filtered = useMemo(() => {
    if (activeTab === 'all') {
      return { results: allResults, alerts: allAlerts, sentiment: data?.sentimentBreakdown };
    }
    if (activeTab === 'personal') {
      if (!selfEntity) return { results: [], alerts: [], sentiment: undefined };
      const results = data?.resultsByEntity?.[selfEntity.id] || [];
      const selfName = selfEntity.name.toLowerCase();
      const alerts = allAlerts.filter(a => a.message?.toLowerCase().includes(selfName));
      return { results, alerts, sentiment: computeSentiment(results) };
    }
    // partnerships
    if (partnerFilter === 'all-partners') {
      const results = partnerEntities.flatMap(e => data?.resultsByEntity?.[e.id] || []);
      const partnerNames = partnerEntities.map(e => e.name.toLowerCase());
      const alerts = allAlerts.filter(a => partnerNames.some(n => a.message?.toLowerCase().includes(n)));
      return { results, alerts, sentiment: computeSentiment(results) };
    }
    const results = data?.resultsByEntity?.[partnerFilter] || [];
    const entityName = partnerEntities.find(e => e.id === partnerFilter)?.name?.toLowerCase() || '';
    const alerts = allAlerts.filter(a => a.message?.toLowerCase().includes(entityName));
    return { results, alerts, sentiment: computeSentiment(results) };
  }, [activeTab, partnerFilter, allResults, allAlerts, data?.resultsByEntity, data?.sentimentBreakdown, entities, selfEntity, partnerEntities]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="relative">
            <Sparkles className="h-12 w-12 text-primary animate-pulse" />
            <div className="absolute inset-0 animate-ping opacity-20">
              <Sparkles className="h-12 w-12 text-primary" />
            </div>
          </div>
          <p className="mt-4 text-lg font-medium">Analyzing firm signal intelligence...</p>
          <p className="text-sm text-muted-foreground mt-1">Fetching news and analyzing sentiment for your firm and portfolio</p>
        </CardContent>
      </Card>
    );
  }

  if (data?.error && allResults.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="h-10 w-10 text-destructive mb-3" />
          <p className="text-destructive font-medium">Failed to load signal intelligence</p>
          <p className="text-sm text-muted-foreground mt-1">{data.error}</p>
          <Button variant="outline" size="sm" onClick={refresh} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <CardTitle className="text-base sm:text-lg">Firm Signal Intelligence</CardTitle>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {entities.length > 0 && (
                <Badge variant="outline" className="gap-1 hidden sm:flex">
                  <CheckCircle className="h-3 w-3" />
                  Monitoring {entities.length} {entities.length === 1 ? 'entity' : 'entities'}
                </Badge>
              )}
              <div className="flex items-center rounded-lg border bg-muted/50 p-0.5">
                {(['7d', '30d', '90d'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setDateRange(range)}
                    className={cn(
                      'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                      dateRange === range
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="icon" onClick={refresh} className="h-8 w-8">
                <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Real-time market intelligence and sentiment for your firm and deal partners
            </p>
            {data?.lastUpdated && (
              <p className="text-xs text-muted-foreground">
                Last updated: {formatDistanceToNow(data.lastUpdated, { addSuffix: true })}
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {allResults.length === 0 && allAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Sparkles className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No recent coverage found</p>
              <Button variant="outline" size="sm" onClick={refresh} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Search Again
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== 'partnerships') setPartnerFilter('all-partners'); }}>
                <div className="flex items-center gap-3 flex-wrap">
                  <TabsList className="w-fit">
                    <TabsTrigger value="all">📊 All</TabsTrigger>
                    <TabsTrigger value="personal">🏦 Personal</TabsTrigger>
                    <TabsTrigger value="partnerships">🤝 Partnerships</TabsTrigger>
                  </TabsList>
                  {activeTab === 'partnerships' && partnerEntities.length > 0 && (
                    <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                      <SelectTrigger className="w-[200px] h-9">
                        <SelectValue placeholder="Filter partners" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all-partners">All Partners</SelectItem>
                        {partnerEntities.map(e => (
                          <SelectItem key={e.id} value={e.id}>
                            {getEntityEmoji(e.type)} {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </Tabs>

              <SignalTabContent
                analyzedResults={filtered.results}
                sentimentBreakdown={filtered.sentiment}
                alerts={filtered.alerts}
                alertsLabel="Strategic Alerts"
                sentimentLabel="Market Sentiment"
                coverageLabel="Industry Coverage"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
